import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "..";
import { SEARCH_DELAY } from "../../constant";
import { formatName } from "../../helper";


/**
 * Load all cleaners and make it possible to manage them
 */
class CleanerManagerStore {
    private sdk: ApplicationSdk;

    public cleanerListLoading: boolean = false;
    public cleanerList: Array<any> = [];
    public sortOrder: number = 1;
    public sortField: string = 'id';
    public searchValue: string = '';
    public available_managers: Array<{ id: number; name: string }> = []; // List of available managers
    public available_trainings: Array<{ id: number; name: string }> = []; // List of available managers
    public filterManager: any = null;
    public filterTraining: any = null;
    public filterTrainingStatus: number = 1;

    public currentPage: number = 1;
    public maxPage: number = 1;
    public totalCount: number = 0;
    public latestPage: number = 1;

    public timeout: any = null;

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            filterManager: observable,
            filterTraining: observable,
            filterTrainingStatus: observable,
            cleanerListLoading: observable,
            cleanerList: observable,
            available_managers: observable,
            available_trainings: observable,
            sortOrder: observable,
            sortField: observable,
            searchValue: observable,
            currentPage: observable,
            maxPage: observable,
            totalCount: observable,
            fetchCleanerList: action,
            onSort: action,
            onSearch: action,
            onDialogLeave: action,
            updateSearch: action,
            clearSearch: action,
            applyFilterManager: action,
            applyFilterTraining: action,
            applyFilterTrainingStatus: action,
            allCleaners: computed,
            filterTrainingLabel: computed,
        });

        this.sdk = sdk;

        // Load state from localStorage
        this.loadFromLocalStorage();
    }

    public applyFilterManager(manager: any): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchCleanerList();
    }

    public applyFilterTraining(training: any): void {
        this.filterTraining = training;
        this.saveToLocalStorage();
        this.fetchCleanerList();
    }

    public applyFilterTrainingStatus(status: any): void {
        this.filterTrainingStatus = status;
        this.saveToLocalStorage();
        this.fetchCleanerList();
    }

    private saveToLocalStorage(): void {
        const state = {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterTraining: this.filterTraining,
            filterTrainingStatus: this.filterTrainingStatus,
            filterManager: this.filterManager
        };
        localStorage.setItem('cleaner', JSON.stringify(state));
    }

    private loadFromLocalStorage(): void {
        const savedState = localStorage.getItem('cleaner');
        if (savedState) {
            try {
                const { sortOrder, sortField, searchValue, filterManager, filterTraining, filterTrainingStatus } = JSON.parse(savedState);
                runInAction(() => {
                    this.sortOrder = sortOrder || 1;
                    this.sortField = sortField || 'id';
                    this.searchValue = searchValue || '';
                    this.filterManager = filterManager || null; // Reset filterManager to null on load
                    this.filterTraining = filterTraining || null; // Reset filterTraining to null on load
                    this.filterTrainingStatus = filterTrainingStatus || 0; // Reset filterTraining to null on load

                });
            } catch (error) {
                console.error('Failed to load state from localStorage:', error);
            }
        }
    }

    public async init(): Promise<void> {
        this.fetchCleanerList();
    }

    public async onSearch(): Promise<void> {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        await this.fetchCleanerList();
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
            this.fetchCleanerList();
        }
    }

    public updateSearch(value: string): void {
        this.searchValue = value;

        // Save updated state to localStorage
        this.saveToLocalStorage();

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (SEARCH_DELAY) {
            this.timeout = setTimeout(() => {
                this.fetchCleanerList();
            }, SEARCH_DELAY);
        }
    }

    public onPageChange(nextPage: number): void {
        this.fetchCleanerList(nextPage);
    }

    public async onSort(sortField: string): Promise<void> {
        runInAction(() => {
            if (this.sortField === sortField) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortOrder = -1;
            }
            this.sortField = sortField;

            // Save updated state to localStorage
            this.saveToLocalStorage();
        });

        await this.fetchCleanerList();
    }

    public async fetchCleanerList(page: number = 1): Promise<void> {
        this.latestPage = page;
        runInAction(() => {
            this.cleanerListLoading = true;
        });

        const cleanerListResult: any = await this.sdk.fetchApi(
            'cleaner',
            'query',
            {
                sortvalue: this.sortField,
                sort: this.sortOrder === 1 ? 'asc' : 'desc',
                search: this.searchValue || '',
                page,
                ...(this.filterManager ? { filter: { type: 'manager', value: this.filterManager.id } } : {}),
                ...(this.filterTraining ? { filterTraining: { id: this.filterTraining.id, passed: this.filterTrainingStatus === 1 } } : {}),
            });

        runInAction(() => {

            this.cleanerListLoading = false;

            if (cleanerListResult?.status === 200) {
                this.cleanerList = cleanerListResult.json.data;
                this.currentPage = cleanerListResult.json.pagination.page;
                this.totalCount = cleanerListResult.json.pagination.total;
                this.maxPage = Math.ceil(this.totalCount / cleanerListResult.json.pagination.pageSize);
            }
        })
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_managers.length > 1) {
                return;
            }
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: formatName(manager.firstname, manager.lastname)
                }));
            }
        });
    }

    public async fetchAvailableTrainings(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_trainings.length > 1) {
                return;
            }
        }
        const managerResponse: any = await this.sdk.fetchApi('training', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_trainings = managerResponse.json.data.map((training: any) => ({
                    id: training.id,
                    name: training.name
                }));
            }
        });
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'cleaner' && event?.event === 'SAVED') {
            await this.fetchCleanerList(this.latestPage);
        }
    }

    get allCleaners(): Array<any> | null {
        if (!this.cleanerList) return null;
        return this.cleanerList.filter((item: any) => {
            return true;
            // return item.name.toLowerCase().includes(this.productQuickSearch.toLowerCase()) || item?.description?.toLowerCase()?.includes(this.productQuickSearch.toLowerCase());
        });
    }

    public onLeaveStore(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    get filterTrainingLabel(): string | null {
        if (this.filterTraining) {
            return `${this.filterTraining.name} (${this.filterTrainingStatus === 1 ? 'Bestanden' : 'Nicht bestanden'})`;
        }
        return null;
    }
}

export default CleanerManagerStore;